import React, { useEffect, useId, useRef, useState } from 'react';
import { MarkButton, MarkInput, MarkLabel } from './MarkControls';
import QueryInlineError from '../../../components/QueryInlineError';
import useMarkTemplate from '../api/useMarkTemplate';
import useMarkTemplates from '../api/useMarkTemplates';
import useDebouncedValue from './BlockEditor/useDebouncedValue';
import useTemplatePreview from '../api/useTemplatePreview';
import { extractBlastErrorMessage } from '../pages/Blasts/blastFormUtils';
import * as styles from './templateAuthoring.module.css';
import type { MarkTemplateRow, RenderBlockDoc } from '../types';
import EmailPreviewFrame from './EmailPreviewFrame';

export const useAsyncLifetime = () => {
    const lifetime = useRef(0);
    useEffect(
        () => () => {
            lifetime.current += 1;
        },
        []
    );
    return lifetime;
};

const LibraryPreview: React.FC<{ row: MarkTemplateRow }> = ({ row }) => {
    const { mutate, data, error, isPending } = useTemplatePreview();
    useEffect(() => {
        const blockDoc: RenderBlockDoc = {
            version: 1,
            blocks: row.blocks,
            preheader: row.preheader
        };
        mutate(
            row.format === 'html'
                ? { html: row.html ?? '', subject: row.subject, preheader: row.preheader }
                : {
                      block_doc: blockDoc,
                      subject: row.subject
                  }
        );
    }, [row, mutate]);
    return (
        <>
            {isPending && <p role="status">Rendering preview…</p>}
            {error && (
                <div role="alert" className={styles.notice}>
                    <p>This template could not be previewed.</p>
                    <p>{extractBlastErrorMessage(error)}</p>
                </div>
            )}
            {data && !error && (
                <EmailPreviewFrame
                    title={`Preview of ${row.name || row.slug}`}
                    html={data.data.html}
                    height={480}
                />
            )}
            <p className={styles.hint}>
                Preview uses sample values. Check personalization for your audience after choosing.
            </p>
        </>
    );
};
const PAGE_SIZE = 8;
const TemplateLibraryPicker: React.FC<{ onPick: (row: MarkTemplateRow) => void }> = ({
    onPick
}) => {
    const filterId = useId();
    const previewRef = useRef<HTMLDivElement>(null);
    const [name, setName] = useState('');
    const [offset, setOffset] = useState(0);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const debouncedName = useDebouncedValue(name, 400);
    const rows = useMarkTemplates({ library: true, name: debouncedName, offset, limit: PAGE_SIZE });
    const detail = useMarkTemplate(selectedId);
    const filtering = name !== debouncedName;
    const choices = filtering ? [] : (rows.data?.data.templates ?? []);
    const row = detail.data?.data;
    const valid =
        row &&
        row.id === selectedId &&
        row.channel === 'email' &&
        row.blast_variant_id == null &&
        row.drip_version_id == null &&
        (row.format === 'html' || row.format === 'blocks');
    const total = rows.data?.data.total_count;
    const page = (next: number) => {
        setSelectedId(null);
        setOffset(next);
    };
    return (
        <section aria-label="Choose a library template" className={styles.library}>
            <div>
                <h3>Choose a template</h3>
                <p className={styles.hint}>
                    Start with a saved email, then make it your own. Library templates stay
                    unchanged.
                </p>
            </div>
            <div className={styles.browser}>
                <div className={styles.results}>
                    <div className={styles.field}>
                        <MarkLabel htmlFor={filterId}>Search templates</MarkLabel>
                        <MarkInput
                            id={filterId}
                            type="search"
                            placeholder="Search by name"
                            value={name}
                            onChange={(event) => {
                                setSelectedId(null);
                                setOffset(0);
                                setName(event.currentTarget.value);
                            }}
                        />
                    </div>
                    {(rows.isFetching || filtering) && <p role="status">Loading templates…</p>}
                    {rows.error && <QueryInlineError error={rows.error} />}
                    {!rows.error &&
                        choices.map((choice) => (
                            <button
                                type="button"
                                key={choice.id}
                                className={styles.result}
                                aria-pressed={selectedId === choice.id}
                                onClick={() => {
                                    setSelectedId(choice.id);
                                    requestAnimationFrame(() => {
                                        if (window.innerWidth <= 850)
                                            previewRef.current?.scrollIntoView({ block: 'start' });
                                    });
                                }}
                            >
                                <span className={styles.resultHeading}>
                                    <strong>{choice.name || choice.slug}</strong>
                                    <span className={styles.badge}>
                                        {choice.format === 'html' ? 'HTML' : 'Blocks'}
                                    </span>
                                </span>
                                <span className={styles.hint}>
                                    {choice.subject || 'No subject'}
                                </span>
                            </button>
                        ))}
                    {!rows.isFetching && !filtering && !rows.error && choices.length === 0 && (
                        <p>No templates found. Try another name or create one in Templates.</p>
                    )}
                    <nav aria-label="Template pages" className={styles.pagination}>
                        <MarkButton
                            disabled={offset === 0 || rows.isFetching || filtering}
                            onClick={() => page(Math.max(0, offset - PAGE_SIZE))}
                        >
                            Previous
                        </MarkButton>
                        <span>
                            {choices.length > 0
                                ? `${offset + 1}–${offset + choices.length}${total == null ? '' : ` of ${total}`}`
                                : '0 results'}
                        </span>
                        <MarkButton
                            disabled={
                                rows.isFetching ||
                                filtering ||
                                !!rows.error ||
                                (total == null
                                    ? choices.length < PAGE_SIZE
                                    : offset + PAGE_SIZE >= total)
                            }
                            onClick={() => page(offset + PAGE_SIZE)}
                        >
                            Next
                        </MarkButton>
                    </nav>
                </div>
                <div
                    ref={previewRef}
                    className={styles.selection}
                    aria-label="Selected template preview"
                >
                    {selectedId == null ? (
                        <div className={styles.previewEmpty}>
                            <h3>Find the right starting point</h3>
                            <p>Select a template to see its email preview before using it.</p>
                        </div>
                    ) : detail.isLoading ? (
                        <p role="status">Loading template…</p>
                    ) : detail.error ? (
                        <QueryInlineError error={detail.error} />
                    ) : valid && row ? (
                        <>
                            <div>
                                <h3>{row.name || row.slug}</h3>
                                <p>
                                    <strong>Subject:</strong> {row.subject}
                                </p>
                                {row.preheader && <p className={styles.hint}>{row.preheader}</p>}
                            </div>
                            <LibraryPreview key={row.id} row={row} />
                            <div className={styles.useTemplate}>
                                <p className={styles.hint}>
                                    This copies the email into your draft. Review it and save to
                                    keep your changes.
                                </p>
                                <MarkButton
                                    variant="primary"
                                    disabled={detail.isFetching || filtering || rows.isFetching}
                                    onClick={() => onPick(row)}
                                >
                                    Use this template
                                </MarkButton>
                            </div>
                        </>
                    ) : (
                        <p role="alert">Choose an email template from the library.</p>
                    )}
                </div>
            </div>
        </section>
    );
};
export default TemplateLibraryPicker;
